import { Fragment, FunctionComponent, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import RoundedButton from 'components/RoundedButton'
import FlaredArrow from 'components/SVG/FlaredArrow'
import StarIcon from 'components/SVG/Star'
import Spacer from 'components/Spacer'

import { QueryParamsContext } from 'services/contexts'

import { addCommas } from 'utils/stringFormatting'

import { TestimonialDataPricingPage as TestimonialData } from 'data/MarketingData/TestimonialData'

const Background = styled.div``

const Header = styled.h3`
  font-size: 32px;
  font-weight: 900;
  line-height: 45px;
  text-align: center;
  margin: 0;
  padding: 0;
  color: ${props => props.theme.colors.white};
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    font-size: 40px;
    line-height: 55px;
  }
`

const SubHeader = styled.div`
  font-size: 18px;
  font-weight: 500px;
  line-height: 25px;
  text-align: center;
  color: ${props => props.theme.colors.white};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    font-size: 20px;
    line-height: 27px;
  }
`

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    flex-direction: row;
  }
`

const Card = styled.div`
  display: flex;
  flex-basis: 30%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 24px;
  padding: 24px 48px;
  z-index: 1;
  box-shadow: 0px 32px 32px ${props => props.theme.colors.dropShadow6};
  background-color: ${props => props.theme.colors.white};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    justify-content: flex-start;
    padding: 24px 32px 40px;
  }
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    max-width: 347px;
    justify-content: flex-start;
    padding: 24px 48px;
  }
`

const PersonIconContainer = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: linear-gradient(115deg, rgba(255, 240, 162, 1) 0%, rgba(255, 205, 84, 1) 100%);
`

const PersonIcon = styled.img`
  height: 100%;
  width: 100%;
  border-radius: 50%;
  padding: 6px;
`

const RatingContainer = styled.div`
  svg {
    margin: 0 6px;
  }
`

const CustomerTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

interface ICustomerTextProps {
  color?: string
  italic?: boolean
}
const CustomerText = styled.div<ICustomerTextProps>`
  font-style: ${props => (props.italic ? 'italic' : 'normal')};
  font-weight: 400;
  font-size: 16px;
  line-height: 25px;
  text-align: center;
  color: ${props => (props.color ? props.color : props.theme.colors.neutral900)};
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    line-height: 22px;
  }
`

const BoldText = styled.span`
  font-weight: 800;
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    flex-direction: row;
  }
`

interface IProps {
  customButton?: JSX.Element
}

const TestimonialsA: FunctionComponent<IProps> = ({ customButton }) => {
  const theme = useContext(ThemeContext)
  // query for appending UTM params
  const { params: query } = useContext(QueryParamsContext)

  const campaignType = 'pricing-page-1'

  const testimonials = TestimonialData.filter(testimonial =>
    Array.isArray(testimonial.campaignType)
      ? testimonial.campaignType.indexOf(campaignType) > -1
      : testimonial.campaignType === campaignType,
  )

  const renderRightArrowIcon = () => (
    <FlaredArrow
      fill={theme.colors.blue4}
      width={15}
      height={15}
    />
  )

  return (
    <Background>
      <div>
        <Header>Happy Sellers</Header>
        <Spacer size={16} />
        <SubHeader>Don&apos;t take it from us, take it from them!</SubHeader>
        <Spacer
          size={50}
          tabletSize={40}
          responsiveSize={36}
        />
        <CardsContainer>
          {testimonials.map((testimonial, index) => {
            const reviews = testimonial.review.split(testimonial.highlight)
            return (
              <Fragment key={testimonial.name}>
                {index > 0 && (
                  <Spacer
                    size={32}
                    responsiveSize={32}
                  />
                )}
                <Card key={`${testimonial.name}-testimonial`}>
                  <PersonIconContainer>
                    <PersonIcon
                      src={testimonial.imgUrl}
                      alt={testimonial.name}
                    />
                  </PersonIconContainer>
                  <Spacer size={32} />
                  <CustomerTextContainer>
                    <RatingContainer>
                      <StarIcon
                        fill={theme.colors.yellow2}
                        width={18}
                        height={18}
                      />
                      <StarIcon
                        fill={theme.colors.yellow2}
                        width={18}
                        height={18}
                      />
                      <StarIcon
                        fill={theme.colors.yellow2}
                        width={18}
                        height={18}
                      />
                      <StarIcon
                        fill={theme.colors.yellow2}
                        width={18}
                        height={18}
                      />
                      <StarIcon
                        fill={theme.colors.yellow2}
                        width={18}
                        height={18}
                      />
                    </RatingContainer>
                    <Spacer size={12} />
                    <CustomerText italic>
                      {reviews[0]}
                      <BoldText>{testimonial.highlight}</BoldText>
                      {reviews[1]}
                    </CustomerText>
                    <Spacer size={16} />
                    <CustomerText color={theme.colors.neutral400}>
                      <div>{`- ${testimonial.name}`}</div>
                      <div>
                        {` sold their home ${
                          testimonial.location ? `in ${testimonial.location}` : ''
                        } and saved `}
                      </div>
                      <div>
                        {`$${addCommas(testimonial.savings)} of ${
                          testimonial.pronoun ?? 'their'
                        } equity.`}
                      </div>
                    </CustomerText>
                  </CustomerTextContainer>
                </Card>
              </Fragment>
            )
          })}
        </CardsContainer>
        <Spacer
          size={56}
          tabletSize={40}
          responsiveSize={36}
        />
        <ButtonsContainer>
          {customButton || (
            <>
              <RoundedButton
                text="See More Reviews"
                href="https://www.zillow.com/profile/HomeLister/#reviews"
                externalLink
                background={theme.colors.blue500}
                color={theme.colors.white}
                boxShadow={`0px 12px 16px ${theme.colors.dropShadow7}`}
                fontSize={12}
                radius={50}
                height={44}
                width={230}
                lineHeight={16}
                bold
              />
              <Spacer size={18} />
              <RoundedButton
                text="Get Started"
                href={{
                  pathname: '/listing/create',
                  query: query as { [key: string]: string },
                }}
                background={theme.colors.white}
                borderColor={theme.colors.blue500}
                borderWidth={2}
                color={theme.colors.blue4}
                boxShadow={`0px 12px 16px ${theme.colors.dropShadow7}`}
                renderRightIcon={renderRightArrowIcon}
                fontSize={12}
                radius={50}
                height={44}
                width={230}
                lineHeight={16}
                bold
              />
            </>
          )}
        </ButtonsContainer>
      </div>
    </Background>
  )
}

export default TestimonialsA
